// Tells a returning visitor about the new game, once. A first-time visitor is
// left alone: everything is new to them. "Returning" means this browser was
// here on an earlier visit, or holds a signed-in account. Once shown, it never
// comes back, whether they tap it, close it or let it fade.
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { trackEvent } from '../../services/analyticsService';
import './NewGameToast.css';

const SEEN = 'opq-seen-new-jigsaw';
const VISITED = 'opq-visited';
// The puzzle went live on 2026-10-21. Anyone whose first visit was before that
// has not seen it yet; anyone newer meets it on the home page anyway.
const LAUNCH = Date.UTC(2026, 9, 21);

const store = (fn) => { try { return fn(); } catch (e) { return null; } };

// When this browser first came: the Google Analytics cookie carries it
// (_ga=GA1.1.<random>.<first-visit seconds>), so visitors from before the
// notice existed count too. (The first version only knew visits after it
// shipped, so Fahad himself never saw it.)
const firstVisit = () => {
  const ga = /(?:^|;\s*)_ga=GA\d\.\d\.\d+\.(\d{9,10})/.exec(document.cookie);
  const fromGa = ga ? Number(ga[1]) * 1000 : null;
  const mine = Number(store(() => localStorage.getItem(VISITED))) || null;
  if (!mine) store(() => localStorage.setItem(VISITED, String(Date.now())));
  return Math.min(fromGa || Infinity, mine || Infinity);
};

const shouldShow = () => store(() => {
  if (localStorage.getItem(SEEN)) return false;
  const signedIn = !!localStorage.getItem('onepiece_quiz_auth');
  return signedIn || firstVisit() < LAUNCH;
}) || false;

// Luffy breaking into 12 pieces and snapping back, on a loop. Drawn by the
// browser from one 8 KB picture: sharp at any size, nothing to download.
const TILES = [];
for (let r = 0; r < 4; r++) for (let c = 0; c < 3; c++) TILES.push({ r, c });
const PuzzleLoop = () => (
  <span className="ngt-puzzle" aria-hidden="true">
    {TILES.map(({ r, c }, i) => (
      <span
        key={i}
        className="ngt-tile"
        style={{
          left: `${(c / 3) * 100}%`,
          top: `${(r / 4) * 100}%`,
          backgroundImage: 'url(/puzzle/thumb/monkey-d-luffy.jpg)',
          backgroundPosition: `${c * 50}% ${r * (100 / 3)}%`,
          // each piece flies its own way and spins a little
          '--dx': `${(c - 1) * 4 + ((i * 7) % 3) - 1}px`,
          '--dy': `${(r - 1.5) * 3 + ((i * 3) % 3) - 1}px`,
          '--rot': `${((i * 37) % 24) - 12}deg`,
          animationDelay: `${(i % 4) * 40}ms`,
        }}
      />
    ))}
  </span>
);

export default function NewGameToast() {
  const { pathname } = useLocation();
  const [state, setState] = useState('waiting'); // waiting → open → gone

  useEffect(() => {
    if (state !== 'waiting') return undefined;
    if (pathname === '/puzzle' || pathname.startsWith('/room')) return undefined;
    if (!shouldShow()) { setState('gone'); return undefined; }
    const t = setTimeout(() => {
      if (document.body.classList.contains('game-fullscreen')) return;
      store(() => localStorage.setItem(SEEN, String(Date.now())));
      setState('open');
      trackEvent('new_game_notice', { action: 'shown', game: 'puzzle' });
    }, 2500);
    return () => clearTimeout(t);
  }, [pathname, state]);

  useEffect(() => {
    if (state !== 'open') return undefined;
    const t = setTimeout(() => setState('gone'), 12000);
    return () => clearTimeout(t);
  }, [state]);

  // Never over a game in play, and gone once they reach the puzzle.
  useEffect(() => {
    if (state === 'open' && pathname === '/puzzle') setState('gone');
  }, [pathname, state]);

  if (state !== 'open') return null;
  return (
    <div className="ngt" role="status">
      <Link to="/puzzle" className="ngt-link" onClick={() => { trackEvent('new_game_notice', { action: 'clicked', game: 'puzzle' }); setState('gone'); }}>
        <PuzzleLoop />
        <span className="ngt-text">
          <span className="ngt-tag">New game</span>
          <span className="ngt-title">One Piece Jigsaw</span>
          <span className="ngt-sub">Race your friends</span>
        </span>
        <span className="ngt-go">Play</span>
      </Link>
      <button type="button" className="ngt-close" aria-label="Close" onClick={() => { trackEvent('new_game_notice', { action: 'closed', game: 'puzzle' }); setState('gone'); }}>×</button>
    </div>
  );
}
